import type { ChangeEvent, HTMLInputTypeAttribute } from "react";
import { useState } from "react";
import { Input } from "../ui/input";

interface InputFieldProps {
  name: string;
  label?: string;
  error?: string;
  readonly?: boolean;
  disable?: boolean;
  autoFocus?: boolean;
  className?: string;
  placeHolder?: string;
  inputClassName?: string;
  type?: HTMLInputTypeAttribute;
  value?: string | number | readonly string[] | undefined;
  defaultValue?: string | number | readonly string[] | undefined;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputMode?: "search" | "text" | "email" | "tel" | "url" | "none" | "numeric" | "decimal" | undefined;
  disableFutureDates?: boolean;
  centerError?: boolean;
  /** Non-editable unit shown inside the input (e.g. "SF"). The user only types the value. */
  unitSuffix?: string;
}

export const InputField = ({
  name,
  type,
  label,
  error,
  autoFocus,
  readonly = false,
  disable = false,
  className,
  placeHolder,
  defaultValue,
  inputClassName,
  onChange,
  inputMode,
  onBlur,
  value,
  onKeyDown,
  disableFutureDates = false,
  centerError = false,
  unitSuffix,
}: InputFieldProps) => {
  const [localValue, setLocalValue] = useState<string>(String(value ?? defaultValue ?? ""));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (type === "number") {
      if (!/^\d*\.?\d*$/.test(newValue)) return;
    }

    setLocalValue(newValue);
    onChange?.({ ...e, target: { ...e.target, value: newValue } });
  };

  const getMaxDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  return (
    <div className={`flex flex-col space-y-1 ${className} ${disable ? "cursor-not-allowed opacity-50" : ""}`}>
      {label && <p className="!p-0">{label}</p>}
      <div className="relative flex w-full items-center">
        <Input
          autoComplete="off"
          autoFocus={autoFocus}
          onKeyDown={onKeyDown}
          inputMode={inputMode}
          onBlur={onBlur}
          readOnly={readonly || disable}
          value={localValue}
          onChange={handleChange}
          name={name}
          type={type === "number" ? "text" : (type ?? "text")}
          placeholder={placeHolder}
          max={type === "date" && disableFutureDates ? getMaxDate() : undefined}
          className={`!focus-visible:ring-0 !focus:outline-none !placeholder:text-base !placeholder:font-normal !placeholder:text-[#ADAEBC] !rounded-lg !border-gray-300 ${unitSuffix ? "!pr-8" : ""} ${inputClassName} ${error ? "!border-orange-500 !bg-orange-50" : ""}`}
        />
        {unitSuffix && (
          <span className="pointer-events-none absolute right-2.5 text-xs font-medium text-muted-foreground select-none">
            {unitSuffix}
          </span>
        )}
      </div>
      {error && (
        <div className={`flex w-full ${centerError ? "justify-center" : ""}`}>
          <p className="text-xs whitespace-nowrap !text-orange-500">{error}</p>
        </div>
      )}
    </div>
  );
};
